import { Injectable } from '@angular/core';
import { CalendarEvent } from 'angular-calendar';
import { ColorProfile, HallPass, Invitation, PassLike, Request, SchoolActivity, SchoolActivityInstance, User } from 'app/models';
import { CalendarEventMeta, CalendarEventResponse, CalendarEventTypeEnum } from 'app/models/CalendarEvent';
import { RequestDatePipe } from 'app/pipes/request-date.pipe';
import * as _ from 'lodash';
import * as moment from 'moment';
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';
import { convertToTimestamp, Util } from 'Util';
import { CalendarService } from './calendar.service';
import { FlexPeriod, FlexPeriodService } from './flex-period.service';
import { HallPassesService } from './hall-passes.service';
import { LiveUpdateService } from './live-update.service';
import { AttendeeData, GetAttendeesForInstanceReq, SchoolActivityAttendee, SchoolActivityService } from './school-activity.service';
import { TimeService } from './time.service';
import { UserService } from './user.service';

const CALCULATED_MINIMUM_MINUTES = 21; // minimum number of minutes for an event to be displayed
const DAYS_SHOW_ACTIVITY_FUTURE_STUDENT = 30;

@Injectable({
	providedIn: 'root',
})
export class CalendarV2Service {
	effectiveUser!: User;
	futurePassesAndActivitiesForStudents$ = new BehaviorSubject<CalendarEvent<CalendarEventMeta>[]>([]);
	currentStudentActivities$ = new BehaviorSubject<CalendarEvent<CalendarEventMeta>[]>([]);
	passesAndActivitiesForWeek$ = new BehaviorSubject<CalendarEvent<CalendarEventMeta>[]>([]);

	private destroy$ = new Subject();

	constructor(
		private calendarService: CalendarService,
		private userService: UserService,
		private hallPassService: HallPassesService,
		private requestDatePipe: RequestDatePipe,
		private schoolActivityService: SchoolActivityService,
		private liveUpdateService: LiveUpdateService,
		private timeService: TimeService,
		private flexPeriodService: FlexPeriodService
	) {}

	fetchEvents(startDate: Date, endDate: Date): Observable<CalendarEvent<CalendarEventMeta>[]> {
		this.calendarService.showLoading$.next(true);
		return this.userService.effectiveUser$.pipe(
			distinctUntilKeyChanged('id'),
			switchMap((user) => {
				this.effectiveUser = user;
				const isStudent = user.isStudent();
				const from = startDate.toISOString();
				const to = endDate.toISOString();

				const events$ = isStudent
					? this.calendarService.getCalendarEventsForStudent(user.id, from, to)
					: this.calendarService.getCalendarEventsForTeacher(user.id, from, to);

				return events$.pipe(
					catchError((err) => {
						console.error('Error retrieving calendar events:', err);
						return of([]);
					})
				);
			}),
			map((calendarEventsResponse: CalendarEventResponse[]) => {
				const parsedEvents: CalendarEvent<CalendarEventMeta>[] = calendarEventsResponse
					.map((eventResponse) => {
						let ce: CalendarEvent<CalendarEventMeta> | null = null;

						switch (eventResponse.type) {
							case CalendarEventTypeEnum.HALL_PASS: {
								const pass = HallPass.fromJSON(eventResponse.hall_pass);
								ce = this.getCalendarEventForHallPass(pass);
								break;
							}
							case CalendarEventTypeEnum.PASS_REQUEST: {
								const passRequest = Request.fromJSON(eventResponse.pass_request);
								ce = this.getCalendarEventForPassRequest(passRequest);
								break;
							}
							case CalendarEventTypeEnum.PASS_INVITATION: {
								const invitation = Invitation.fromJSON(eventResponse.pass_invitation);
								if (invitation) {
									ce = this.getCalendarEventForPassInvitation(invitation);
								}
								break;
							}
							case CalendarEventTypeEnum.SIGN_UP:
							case CalendarEventTypeEnum.ATTENDING:
							case CalendarEventTypeEnum.ACTIVITY:
							case CalendarEventTypeEnum.ACTIVITY_INSTANCE: {
								ce = this.getCalendarEventForFlex(eventResponse);
								break;
							}
						}

						return ce; // do NOT spread eventResponse
					})
					.filter((e): e is CalendarEvent<CalendarEventMeta> => e !== null);
				this.passesAndActivitiesForWeek$.next(parsedEvents);
				return parsedEvents;
			}),
			tap(() => this.calendarService.showLoading$.next(false))
		);
	}

	filterAndTrackActivitiesFromCalendarEvents(events: CalendarEvent<CalendarEventMeta>[]): CalendarEvent<CalendarEventMeta>[] {
		const now = this.timeService.now();
		const currentActivities: CalendarEvent<CalendarEventMeta>[] = [];

		if (!Array.isArray(events)) {
			this.currentStudentActivities$.next([]);
			return [];
		}

		const filteredEvents = events.filter((event) => {
			// Filter Hall Passes for future
			if (event.meta?.type === CalendarEventTypeEnum.HALL_PASS && event.meta?.hallPass) {
				const start = convertToTimestamp(event.meta.hallPass.start_time);
				return start !== null && start > now;
			}

			// Handle SIGN_UP or ATTENDING (i.e., activity types)
			if (event.meta?.type === CalendarEventTypeEnum.SIGN_UP || event.meta?.type === CalendarEventTypeEnum.ATTENDING) {
				const start = convertToTimestamp(event.start);
				const end = convertToTimestamp(event.end);
				const isStarted = start !== null && end !== null && start <= now && now < end;

				if (isStarted) {
					currentActivities.push(event);
					return false;
				}

				return start !== null && start > now;
			}

			// Exclude all others
			return false;
		});
		this.currentStudentActivities$.next(currentActivities);
		return filteredEvents;
	}

	getCalendarEventForHallPass(pass: HallPass): CalendarEvent {
		const index = this.getNextEventId();
		const ce = this.getBaseCalendarEvent(
			index,
			pass.start_time,
			pass.expiration_time,
			pass.destination.title,
			false,
			CalendarEventTypeEnum.HALL_PASS,
			!!pass.schedule_config_id
		);
		const passStatus = this.hallPassService.getPassStatus(pass.start_time, pass.end_time, pass.expiration_time, false);
		if (ce.meta) {
			ce.meta.isOvertime = passStatus === 'overtime';
			ce.meta.isActiveHallPass = passStatus === 'active';
			ce.meta.isEndedHallPass = passStatus === 'ended';
			ce.meta.isPendingScheduledPass = passStatus === 'upcoming' && !!pass.schedule_config_id;
			ce.meta.hallPass = pass;
			ce.meta.borderColor = this.getBorderColor(pass);
			ce.meta.background = this.getBackgroundColor(pass, false);
			ce.meta.eventTitleFontColor = this.getTitleFontColor(pass);
			ce.meta.eventTimeFontColor = this.getTimeFontColor(pass);
		}
		return ce;
	}

	getCalendarEventForPassInvitation(passInvitation: Invitation): CalendarEvent {
		const endTime = moment(passInvitation.date_choices[0]).add(passInvitation.duration, 'seconds');
		const index = this.getNextEventId();
		const ce = this.getBaseCalendarEvent(
			index,
			passInvitation.date_choices[0],
			endTime.toDate(),
			passInvitation.destination.title,
			false,
			CalendarEventTypeEnum.PASS_INVITATION,
			false
		);
		let isMissed = false;
		const requestDate = this.requestDatePipe.transform(passInvitation);
		if (requestDate !== passInvitation.created && Util.invalidDate(new Date(requestDate))) {
			isMissed = true;
		}
		if (ce.meta) {
			ce.meta.pass_invitation = passInvitation;
			ce.meta.isMissed = isMissed;
			ce.meta.borderColor = this.getBorderColor(passInvitation);
			ce.meta.background = this.getBackgroundColor(passInvitation, isMissed);
			ce.meta.eventTitleFontColor = this.getTitleFontColor(passInvitation);
			ce.meta.eventTimeFontColor = this.getTimeFontColor(passInvitation);
		}
		return ce;
	}

	getCalendarEventForPassRequest(passRequest: Request): CalendarEvent {
		const endTime = moment(passRequest.request_time).add(passRequest.duration, 'seconds');
		const index = this.getNextEventId();
		const ce = this.getBaseCalendarEvent(
			index,
			passRequest.request_time,
			endTime.toDate(),
			passRequest.destination.title,
			false,
			CalendarEventTypeEnum.PASS_REQUEST,
			!!passRequest.hallpass?.schedule_config_id
		);
		let isMissed = false;
		const requestDate = this.requestDatePipe.transform(passRequest);
		if (requestDate !== passRequest.created && Util.invalidDate(new Date(requestDate))) {
			isMissed = true;
		}
		if (ce.meta) {
			ce.meta.pass_request = passRequest;
			ce.meta.isMissed = isMissed;
			ce.meta.borderColor = this.getBorderColor(passRequest);
			ce.meta.background = this.getBackgroundColor(passRequest, isMissed);
			ce.meta.eventTitleFontColor = this.getTitleFontColor(passRequest);
			ce.meta.eventTimeFontColor = this.getTimeFontColor(passRequest);
		}
		return ce;
	}

	getCalendarEventForFlex(eventResponse: CalendarEventResponse): CalendarEvent {
		const index = this.getNextEventId();
		const end = eventResponse.end || new Date();

		const ce: CalendarEvent<CalendarEventMeta> = this.getBaseCalendarEvent(
			index,
			eventResponse.start,
			end,
			eventResponse.title,
			eventResponse.allDay,
			eventResponse.type as CalendarEventTypeEnum,
			!!eventResponse.hall_pass?.schedule_config_id,
			eventResponse.flex_period_name
		);
		if (!ce.meta) {
			return ce;
		}

		if (eventResponse.assigner_id) {
			ce.meta.assignerId = eventResponse.assigner_id;
		}

		switch (eventResponse.type) {
			case CalendarEventTypeEnum.SIGN_UP: {
				ce.meta.flexPeriodName = eventResponse.flex_period_name;
				ce.meta.flexPeriodId = eventResponse.flex_period_id;

				const futureLimit = new Date();
				futureLimit.setDate(futureLimit.getDate() + DAYS_SHOW_ACTIVITY_FUTURE_STUDENT);

				// Remove this code when new flex features are GA.
				if (ce.start > futureLimit) {
					ce.meta.preventStudentOpeningMsg = `Sign up starts ${DAYS_SHOW_ACTIVITY_FUTURE_STUDENT} days before the activity.`;
				}
				break;
			}

			case CalendarEventTypeEnum.ATTENDING: {
				ce.meta.flexPeriodName = eventResponse.flex_period_name;
				ce.meta.flexPeriodId = eventResponse.flex_period_id;
				ce.meta.activityId = eventResponse.school_activity_instance?.activity_id;
				ce.meta.schoolActivityInstance = eventResponse.school_activity_instance;
				ce.meta.activityInstanceId = eventResponse.school_activity_instance?.id;
				ce.meta.schoolActivity = eventResponse.school_activity;
				ce.meta.borderColor = 'transparent';
				ce.meta.locationId = eventResponse.school_activity?.location_id;

				if (eventResponse.school_activity_attendee) {
					ce.meta.activityId = eventResponse.school_activity_attendee.activity_id;
				}

				if (ce.meta.assignerId && this.effectiveUser?.id !== ce.meta.assignerId) {
					ce.meta.preventStudentOpeningMsg = 'Teacher added you to ' + ce.title;
				}
				break;
			}

			case CalendarEventTypeEnum.ACTIVITY:
			case CalendarEventTypeEnum.ACTIVITY_INSTANCE: {
				ce.meta.schoolActivityInstance = eventResponse.school_activity_instance;
				ce.meta.schoolActivity = eventResponse.school_activity;
				ce.meta.borderColor = 'transparent';
				ce.meta.locationId = eventResponse.school_activity?.location_id;
				break;
			}
		}

		return ce;
	}

	private parseColorProfile(profile?: ColorProfile | null): ColorProfile | undefined {
		return profile ? ColorProfile.fromJSON(profile) : undefined;
	}

	getGradientBackground(gradient: string[] | null, endDate: Date | undefined): string {
		let base = 'linear-gradient(0deg, rgba(128, 128, 128, 0.80) 0%, rgba(128, 128, 128, 0.80) 100%)'; // default if no gradient provided
		if (gradient && gradient.length >= 2) {
			base = `radial-gradient(118.16% 118.16% at 43.17% 65.58%, ${gradient[0]} 0%, ${gradient[1]} 100%)`;
		}

		if (Util.invalidDate(new Date(endDate || ''))) {
			return `linear-gradient(0deg, rgba(255, 255, 255, 0.80) 0%, rgba(255, 255, 255, 0.80) 100%), ${base}`;
		}

		return base;
	}

	shortDuration(duration: number | undefined): boolean {
		if (duration === undefined) {
			return false;
		}
		return duration < CALCULATED_MINIMUM_MINUTES;
	}

	private getTitleFontColor(pass: PassLike): string {
		if (pass instanceof Request) {
			return pass.color_profile.solid_color;
		}
		if (pass instanceof Invitation) {
			if (pass.status === 'accepted') {
				return '#FFF';
			}
			return pass.color_profile.solid_color;
		}
		if (pass instanceof HallPass) {
			const hpStatus = this.hallPassService.getPassStatus(pass.start_time, pass.end_time, pass.expiration_time, false);
			if (hpStatus === 'overtime') {
				return '#FFFFFF';
			}
			switch (hpStatus) {
				case 'ended':
					return '#7083A0';
					break;
				case 'upcoming':
				case 'active':
					return '#FFFFFF';
					break;
			}
		}
		return '#7083A0';
	}

	private getTimeFontColor(pass: PassLike): string {
		if (pass instanceof Request) {
			return pass.color_profile.solid_color;
		}
		if (pass instanceof Invitation) {
			if (pass.status === 'accepted') {
				return '#FFF';
			}
			return pass.color_profile.solid_color;
		}
		if (pass instanceof HallPass) {
			const hpStatus = this.hallPassService.getPassStatus(pass.start_time, pass.end_time, pass.expiration_time, false);
			if (hpStatus === 'overtime') {
				return '#EAEDF1';
			}
			switch (hpStatus) {
				case 'ended':
					return '#7083A0';
					break;
				case 'upcoming':
				case 'active':
					return '#EAEDF1';
					break;
			}
		}
		return '#7083A0';
	}

	private getBackgroundColor(pass: PassLike, isMissed = false): string {
		if (isMissed) {
			return '#F0F2F5';
		}
		if (pass instanceof HallPass) {
			const hpStatus = this.hallPassService.getPassStatus(pass.start_time, pass.end_time, pass.expiration_time, false);
			if (hpStatus === 'overtime') {
				return 'linear-gradient(139deg, #E32C66 0%, #CB1B53 100%)';
			}
			const colors = pass.color_profile.gradient_color.split(',');
			switch (hpStatus) {
				case 'ended':
					return `linear-gradient(0deg, rgba(255, 255, 255, 0.80) 0%, rgba(255, 255, 255, 0.80) 100%), radial-gradient(144.10% 144.10% at 72.94% 71.48%, ${colors[0]} 0%, ${colors[1]} 100%)`;
				case 'active':
				case 'upcoming':
					return `radial-gradient(144.10% 144.10% at 72.94% 71.48%, ${colors[0]} 0%, ${colors[1]} 100%)`;
			}
		} else if (pass instanceof Request) {
			const status = pass.status;
			switch (status) {
				case 'pending':
					return '#FFFFFF';
				case 'declined':
					return '#F0F2F5';
			}
		} else if (pass instanceof Invitation) {
			const status = pass.status;
			const colors = pass.color_profile.gradient_color.split(',');
			switch (status) {
				case 'accepted':
					return `radial-gradient(144.10% 144.10% at 72.94% 71.48%, ${colors[0]} 0%, ${colors[1]} 100%)`;
				case 'declined':
					return '#F0F2F5';
			}
		}
		return '#FFFFFF';
	}

	private getBorderColor(pass: PassLike): string {
		if (pass instanceof HallPass) {
			const hpStatus = this.hallPassService.getPassStatus(pass.start_time, pass.end_time, pass.expiration_time, false);
			switch (hpStatus) {
				case 'ended':
					return 'transparent';
				case 'upcoming':
				case 'active':
					return 'transparent';
				case 'pending':
					break;
			}
		}
		return `${pass.color_profile.solid_color}33`;
	}

	getNextEventId(): number {
		const latestId = this.calendarService.calendarEventIds.size;
		this.calendarService.addCalendarEventId(latestId + 1);
		return latestId + 1;
	}

	getBaseCalendarEvent(
		index: string | number,
		start: Date,
		end: Date,
		title: string,
		allDay = false,
		type: CalendarEventTypeEnum,
		recurringPass: boolean,
		flex_period_name?: string
	): CalendarEvent<CalendarEventMeta> {
		// adjust ends times to allow calendar events to display at a bigger height
		let dstAdjustedStartTime = new Date(start);
		let dstAdjustedEndTime = new Date(end);
		// hall passes need to be adjusted for daylight savings
		if (type === CalendarEventTypeEnum.PASS_REQUEST || (type === CalendarEventTypeEnum.HALL_PASS && recurringPass)) {
			dstAdjustedStartTime = this.calendarService.adjustDateForDaylightSavings(new Date(start));
			dstAdjustedEndTime = this.calendarService.adjustDateForDaylightSavings(new Date(end));
		}
		const startTime = dstAdjustedStartTime.getTime();
		const endTime = dstAdjustedEndTime.getTime();
		const duration = (endTime - startTime) / 1000 / 60; // duration of activity in minutes
		const eventEnd = new Date(dstAdjustedEndTime);
		// set the height of the event - calendar hours are 100px in height,
		// so figure out the height the event should be in number of pixels
		const calculatedHeight = (duration * 100) / 60;
		let height = 'auto';
		// attending flex activity calendar events have a minimum 52px height
		if (type === CalendarEventTypeEnum.ATTENDING) {
			height = '52px';
		}
		// only use the calculated height if the event is over 21 minutes
		if (calculatedHeight > CALCULATED_MINIMUM_MINUTES) {
			height = `${calculatedHeight}px`;
		}
		const ce: CalendarEvent<CalendarEventMeta> = {
			id: index,
			start: dstAdjustedStartTime,
			end: eventEnd,
			title: title,
			// override default calendar color settings - background colors are handled by
			// targeting a different div in the template/css
			color: { primary: 'transparent', secondary: 'transparent' },
			allDay: allDay,
			cssClass: this.getCssClass(type),
			meta: {
				type: type as CalendarEventTypeEnum,
				height: height,
				displayStartTime: dstAdjustedStartTime,
				displayEndTime: dstAdjustedEndTime,
				flexPeriodName: flex_period_name,
				duration: duration,
			},
		};
		return ce;
	}

	private getCssClass(type: CalendarEventTypeEnum): string {
		switch (type) {
			case CalendarEventTypeEnum.ATTENDING:
				return 'activity';
			case CalendarEventTypeEnum.SIGN_UP:
				return 'flex-period';
			case CalendarEventTypeEnum.ACTIVITY:
			case CalendarEventTypeEnum.ACTIVITY_INSTANCE:
				return 'flex-activity';
			case CalendarEventTypeEnum.HALL_PASS:
				return 'hall-pass';
			case CalendarEventTypeEnum.PASS_REQUEST:
				return 'hall-pass pass-request';
			case CalendarEventTypeEnum.PASS_INVITATION:
				return 'hall-pass pass-invitation';
		}
	}

	//#region Live Update

	watchCalendarEventsForStudent(userId: number): Observable<any> {
		const attendeeCreationPipe = this.createAttendeeCreationPipeForStudent(userId);
		const attendeeDeletionPipe = this.createAttendeeDeletionPipeForStudent(userId);
		const hallPassPipe = this.createHallPassPipeForStudent(userId);
		const requestPipe = this.createRequestPipeForStudent(userId);
		const invitationPipe = this.createPassInvitationPipeForStudent(userId);
		return this.liveUpdateService.isConnected$.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			switchMap(() => {
				return merge(
					this.liveUpdateService.listen('hall_pass.create').pipe(hallPassPipe),
					this.liveUpdateService.listen('hall_pass.delete').pipe(hallPassPipe),
					this.liveUpdateService.listen('hall_pass.start').pipe(hallPassPipe),
					this.liveUpdateService.listen('hall_pass.end').pipe(hallPassPipe),
					this.liveUpdateService.listen('hall_pass.cancel').pipe(hallPassPipe),

					this.liveUpdateService.listen('pass_request.create').pipe(requestPipe),
					this.liveUpdateService.listen('pass_request.delete').pipe(requestPipe),
					this.liveUpdateService.listen('pass_request.accept').pipe(requestPipe),
					this.liveUpdateService.listen('pass_request.deny').pipe(requestPipe),
					this.liveUpdateService.listen('pass_request.cancel').pipe(requestPipe),

					this.liveUpdateService.listen('pass_invitation.create').pipe(invitationPipe),
					this.liveUpdateService.listen('pass_invitation.delete').pipe(invitationPipe),
					this.liveUpdateService.listen('pass_invitation.accept').pipe(hallPassPipe), // backend returns a hall pass for this event
					this.liveUpdateService.listen('pass_invitation.deny').pipe(invitationPipe),
					this.liveUpdateService.listen('pass_invitation.cancel').pipe(invitationPipe),

					this.liveUpdateService.listen('school_activity.create'),
					this.liveUpdateService.listen('school_activity.update'),
					this.liveUpdateService.listen('school_activity.delete'),
					this.liveUpdateService.listen('school_activity_attendees.create').pipe(attendeeCreationPipe),
					this.liveUpdateService.listen('school_activity_attendees.delete').pipe(attendeeDeletionPipe),
					this.liveUpdateService.listen('school_activity_instances.delete'),

					this.liveUpdateService.listen('flex_period.update')
				);
			})
		);
	}

	private createHallPassPipeForStudent(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				map((pe) => pe.data),
				map((data) => {
					if (Array.isArray(data)) {
						return data.map((hp) => HallPass.fromJSON(hp));
					} else {
						return HallPass.fromJSON(data);
					}
				}),
				filter((hp) => {
					if (Array.isArray(hp)) {
						return hp.filter((p) => p.student.id === userId && !p.activity_instance_id).length > 0;
					} else {
						return hp.student.id === userId && !hp.activity_instance_id;
					}
				}),
				catchError((e, originalObs) => {
					console.log('Error in watchCalendarEventsForStudent', e);
					return originalObs;
				})
			);
	}

	private createRequestPipeForStudent(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				map((pe) => pe.data),
				map((data) => {
					if (Array.isArray(data)) {
						return data.map((pr) => {
							// todo sometimes the backend sends the event without teachers
							// figure out if this workaround is ok or if backend should be updated
							if (!pr.teachers) {
								pr.teachers = [];
							}
							return Request.fromJSON(pr);
						});
					} else {
						// todo sometimes the backend sends the event without teachers
						// figure out if this workaround is ok or if backend should be updated
						if (!data.teachers) {
							data.teachers = [];
						}
						return Request.fromJSON(data);
					}
				}),
				filter((pr) => {
					if (Array.isArray(pr)) {
						return pr.filter((p) => p.student.id === userId).length > 0;
					} else {
						return pr.student.id === userId;
					}
				}),
				catchError((e, originalObs) => {
					console.log('Error in watchCalendarEventsForStudent', e);
					return originalObs;
				})
			);
	}

	private createPassInvitationPipeForStudent(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				map((pe) => pe.data),
				map((data) => {
					if (Array.isArray(data)) {
						return data.map((inv) => {
							return Invitation.fromJSON(inv);
						});
					} else {
						return Invitation.fromJSON(data);
					}
				}),
				filter((inv) => {
					if (Array.isArray(inv)) {
						return inv.filter((p) => p?.student.id === userId).length > 0;
					} else {
						return inv?.student.id === userId;
					}
				}),
				catchError((e, originalObs) => {
					console.log('Error in watchCalendarEventsForStudent', e);
					return originalObs;
				})
			);
	}

	private createAttendeeCreationPipeForStudent(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				filter((pe) => {
					if (Array.isArray(pe.data)) {
						return pe.data.filter((p: AttendeeData) => p.user_ids?.includes(userId)).length > 0;
					} else {
						return pe.data.user_ids?.includes(userId);
					}
				}),
				catchError((e, originalObs) => {
					console.log('Error in watchCalendarEventsForStudent', e);
					return originalObs;
				})
			);
	}

	private createAttendeeDeletionPipeForStudent(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				filter((pe, data) => {
					return pe.data.user_id === userId;
				}),
				catchError((e, originalObs) => {
					console.log('Error in watchCalendarEventsForStudent', e);
					return originalObs;
				})
			);
	}

	handleFlexPeriodUpdateForStudentAndRefresh(flexPeriod: FlexPeriod, refreshFn?: (updatedEvent: CalendarEvent<CalendarEventMeta>[]) => void): void {
		const currentEvents = this.passesAndActivitiesForWeek$.value;

		const updatedEvents = currentEvents.map((calendarEvent) => {
			if (calendarEvent.meta?.flexPeriodId === flexPeriod.id) {
				const updatedEvent = {
					...calendarEvent,
					meta: {
						...calendarEvent.meta,
						flexPeriodName: flexPeriod.name,
					},
				};

				if (calendarEvent.meta.type === CalendarEventTypeEnum.SIGN_UP) {
					updatedEvent.title = flexPeriod.name;
				}

				return updatedEvent;
			}
			return calendarEvent;
		});
		this.passesAndActivitiesForWeek$.next(updatedEvents);
		if (refreshFn) {
			refreshFn(updatedEvents);
		}
	}

	handleActivityUpdateForStudent(schoolActivity: SchoolActivity, refreshFn?: (updatedEvent: CalendarEvent<CalendarEventMeta>) => void): void {
		const currentEvents = this.passesAndActivitiesForWeek$.value;

		const updatedEvents = currentEvents.map((calendarEvent) => {
			if (calendarEvent.meta?.activityId === schoolActivity.id) {
				const updatedEvent: CalendarEvent<CalendarEventMeta> = {
					...calendarEvent,
					title: schoolActivity.name,
					meta: {
						...calendarEvent.meta,
						schoolActivity: schoolActivity,
						borderColor: 'transparent',
					},
				};

				const colorProfile = this.parseColorProfile(schoolActivity.color_profile);
				const gradient = colorProfile?.gradient_color?.split(',');

				if (gradient && updatedEvent?.meta) {
					updatedEvent.meta.background = this.getGradientBackground(gradient, updatedEvent.end);
				}

				// Optionally call a refresh callback (for calendar view DOM update)
				if (refreshFn) {
					refreshFn(updatedEvent);
				}

				return updatedEvent;
			}
			return calendarEvent;
		});

		this.passesAndActivitiesForWeek$.next(updatedEvents);
	}

	async handleAddStudentToActivityInstance(
		attendeeData: AttendeeData,
		refreshFn?: (updatedEvent: CalendarEvent<CalendarEventMeta>[]) => void
	): Promise<void> {
		const req: GetAttendeesForInstanceReq = {
			activity_instance_id: attendeeData.school_activity_instance_id,
			start_time: attendeeData.start_time,
			include_user_profiles: false,
		};

		this.schoolActivityService.GetAttendeesForInstance(req);

		const activity = await firstValueFrom(
			this.schoolActivityService.GetActivityByIdHTTP(attendeeData.school_activity_id).pipe(takeUntil(this.destroy$))
		);

		if (!activity) return;

		let matchFound = false;

		const updatedEvents = this.passesAndActivitiesForWeek$.value.map((ce) => {
			if (ce.meta?.flexPeriodId === activity.flex_period_id && moment(ce.start).isSame(moment(attendeeData.start_time), 'day')) {
				matchFound = true;

				const updatedEvent: CalendarEvent<CalendarEventMeta> = {
					...ce,
					cssClass: this.getCssClass(CalendarEventTypeEnum.ATTENDING),
					meta: {
						...ce.meta,
						type: CalendarEventTypeEnum.ATTENDING,
						activityInstanceId: attendeeData.school_activity_instance_id,
						activityId: activity.id,
						assignerId: attendeeData.assigner_id,
						schoolActivity: activity,
						borderColor: 'transparent',
					},
					title: activity.name,
				};

				const colorProfile = this.parseColorProfile(activity.color_profile);
				const gradient = colorProfile?.gradient_color?.split(',');

				if (gradient && updatedEvent?.meta) {
					updatedEvent.meta.background = this.getGradientBackground(gradient, updatedEvent.end);
				}

				return updatedEvent;
			}
			return ce;
		});
		if (!matchFound) {
			const activityInstance = await firstValueFrom(
				this.schoolActivityService.GetActivityInstanceByIdHTTP(attendeeData.school_activity_instance_id).pipe(takeUntil(this.destroy$))
			);
			const newEvent: CalendarEventResponse = {
				type: CalendarEventTypeEnum.ATTENDING,
				start: moment(attendeeData.start_time).toDate(),
				end: moment(attendeeData.end_time).toDate(),
				title: activity.name,
				allDay: false,
				assigner_id: attendeeData.assigner_id,
				flex_period_id: activity.flex_period_id,
				flex_period_name: '',
				location_id: activity.location_id,
				school_activity: activity,
				school_activity_instance: activityInstance,
			};

			if (activity.schedule_type === 'flex_period') {
				const flexPeriod = await firstValueFrom(this.flexPeriodService.flexPeriod$(activity.flex_period_id));
				newEvent.flex_period_name = flexPeriod?.name ?? '';
			}

			const flexEvent = this.getCalendarEventForFlex(newEvent);
			updatedEvents.push(flexEvent);
		}
		this.passesAndActivitiesForWeek$.next(updatedEvents);

		if (refreshFn) {
			refreshFn(updatedEvents);
		}
	}

	handleRemoveStudentFromActivityInstance(attendee: SchoolActivityAttendee, refreshFn?: () => void): void {
		const currentEvents = this.passesAndActivitiesForWeek$.value;

		const updatedEvents = currentEvents
			.map((ce) => {
				if (ce.meta?.activityInstanceId !== attendee.activity_instance_id) {
					return ce;
				}

				// If custom and student not attending, remove the event
				if (ce.meta.schoolActivity?.schedule_type === 'custom') {
					return null;
				}

				const restMeta = _.omit(ce.meta, ['borderColor', 'background', 'activityInstanceId', 'schoolActivityInstance', 'schoolActivity']);
				return {
					...ce,
					cssClass: this.getCssClass(CalendarEventTypeEnum.SIGN_UP),
					meta: {
						...restMeta,
						type: CalendarEventTypeEnum.SIGN_UP,
						assignerId: undefined,
					},
					title: restMeta.flexPeriodName || ce.title,
				};
			})
			.filter((ce): ce is CalendarEvent<CalendarEventMeta> => ce !== null);
		this.passesAndActivitiesForWeek$.next(updatedEvents);

		if (refreshFn) {
			refreshFn();
		}
	}

	removeActivityInstanceForStudent(activityInstance: SchoolActivityInstance, eventList$: BehaviorSubject<CalendarEvent<CalendarEventMeta>[]>): void {
		const currentEvents = eventList$.value;

		const updatedEvents = currentEvents.map((ce) => {
			if (ce.meta?.activityInstanceId === activityInstance.id) {
				// Destructure meta and remove unnecessary properties
				const { borderColor, background, activityInstanceId, schoolActivityInstance, schoolActivity, ...restMeta } = ce.meta;

				return {
					...ce,
					cssClass: this.getCssClass(CalendarEventTypeEnum.SIGN_UP),
					meta: {
						...restMeta,
						type: CalendarEventTypeEnum.SIGN_UP,
						assignerId: undefined,
					},
					title: restMeta.flexPeriodName || ce.title, // reset title to flex period name if available
				};
			}
			return ce;
		});
		eventList$.next(updatedEvents);
	}

	//#endregion
}
